import { sumBy } from 'lodash-es';
import urlJoin from 'url-join';
import { useKarteItemsInfo } from '~/composables/karte/userKarteItemsInfo';
import { useStore } from '~/composables/useStore';
import { PRODUCER_CATEGORIES } from '~/constants';
import { karteTrack, nonNullable, url } from '~/utils';
import { ArticleCategory, ContentProducerArea, KarteProducer, KarteProducerArticle } from '~/types/modules/contents';
import { ArticleType } from '~/enums';
import { useRuntimeConfig } from '../useRuntimeConfig';

// 最新の20件を送る
const NUM_OF_FAV_FOR_KARTE = 20;

export const useKarteFavoriteTag = () => {
  const store = useStore();
  const itemsInfo = useKarteItemsInfo();
  const { FRONTEND_ORIGIN } = useRuntimeConfig();

  const filterItems = (items: any[]) => {
    const filteredItems = items.filter(nonNullable);
    return filteredItems.length > 0 ? filteredItems : undefined;
  };

  const setKarteFavoriteProductsTag = async (itemCode: string, isActive: boolean) => {
    const productCodes = store.state.user.favorites.favoriteItems
      .slice(-NUM_OF_FAV_FOR_KARTE)
      .map(({ favoriteCode }) => favoriteCode);
    let items = [];
    try {
      if (productCodes.length) {
        const res = await store.dispatch('modules/product/fetchProductsVilla', {
          q: `product_code_s:(${productCodes.join(' OR ')})`,
        });
        items = res.items;
      }
    } catch {
      items = [];
    }

    const parsedItems = itemsInfo.getParsedItems(items);

    const infoToBeSent = {
      price: sumBy(items, (item: any) => item.lowest_price_general_exclude_tax_i || 0),
      tax_included_price: sumBy(items, (item: any) => item.lowest_price_general_i || 0),
      quantity: parsedItems.length,
      added_item_id: isActive ? itemCode : undefined,
      deleted_item_id: isActive ? undefined : itemCode,
      status: parsedItems.length > 0,
      items: parsedItems,
      item_item_ids: filterItems(parsedItems.map((item: any) => item.item_id)),
      item_sku_ids: filterItems(parsedItems.map((item: any) => item.sku_id)),
      item_names: filterItems(parsedItems.map((item: any) => item.name)),
      item_prices: filterItems(parsedItems.map((item: any) => item.price)),
      item_item_urls: filterItems(parsedItems.map((item: any) => item.item_url)),
      item_image_urls: filterItems(parsedItems.map((item: any) => item.image_url)),
      item_item_types: filterItems(parsedItems.map((item: any) => item.item_type)),
      item_parent_category_ids: filterItems(parsedItems.map((item: any) => item.parent_category_id)),
      item_parent_category_names: filterItems(parsedItems.map((item: any) => item.parent_category_name)),
      item_category_ids: filterItems(parsedItems.map((item: any) => item.category_id)),
      item_category_names: filterItems(parsedItems.map((item: any) => item.category_name)),
      item_area1_ids: filterItems(parsedItems.map((item: any) => item.area1_id)),
      item_area1_names: filterItems(parsedItems.map((item: any) => item.area1_name)),
      item_area2_ids: filterItems(parsedItems.map((item: any) => item.area2_id)),
      item_area2_names: filterItems(parsedItems.map((item: any) => item.area2_name)),
      item_area3_ids: filterItems(parsedItems.map((item: any) => item.area3_id)),
      item_area3_names: filterItems(parsedItems.map((item: any) => item.area3_name)),
      item_producer_ids: filterItems(parsedItems.map((item: any) => item.producer_id)),
      item_producer_names: filterItems(parsedItems.map((item: any) => item.producer_name)),
      item_base_item_codes: filterItems(parsedItems.map((item: any) => item.base_item_code)),
      item_item_sizes: filterItems(parsedItems.map((item: any) => item.item_size)),
      item_color_group_ids: filterItems(parsedItems.map((item: any) => item.color_group_id)),
      item_color_group_names: filterItems(parsedItems.map((item: any) => item.color_group_name)),
      item_color_ids: filterItems(parsedItems.map((item: any) => item.color_id)),
      item_color_names: filterItems(parsedItems.map((item: any) => item.color_name)),
      item_brand1_ids: filterItems(parsedItems.map((item: any) => item.brand1_id)),
      item_brand2_ids: filterItems(parsedItems.map((item: any) => item.brand2_id)),
      item_brand3_ids: filterItems(parsedItems.map((item: any) => item.brand3_id)),
      item_taste_gauges: filterItems(parsedItems.map((item: any) => item.taste_gauge)),
      item_sense_types: filterItems(parsedItems.map((item: any) => item.sense_type)),
      item_taste1s: filterItems(parsedItems.map((item: any) => item.taste1)),
      item_taste2s: filterItems(parsedItems.map((item: any) => item.taste2)),
      item_taste3s: filterItems(parsedItems.map((item: any) => item.taste3)),
      item_taste4s: filterItems(parsedItems.map((item: any) => item.taste4)),
      item_taste5s: filterItems(parsedItems.map((item: any) => item.taste5)),
      item_taste6s: filterItems(parsedItems.map((item: any) => item.taste6)),
      item_review_points: filterItems(parsedItems.map((item: any) => item.review_point)),
      item_carriage_discount_types: filterItems(parsedItems.map((item: any) => item.carriage_discount_type)),
      item_list_display_flgs: filterItems(parsedItems.map((item: any) => item.list_display_flg)),
    };

    karteTrack('favorite', infoToBeSent);
  };

  // 最新登録順で生産者20件を送信
  const setKarteFavoriteProducersTag = async (producerId: number, isActive: boolean) => {
    // NOTE: お気に入りAPIのレスポンスはすでに登録降順になってる
    const producerIds = store.state.user.favorites.favoriteProducers.map(({ favoriteCode }) => favoriteCode);
    /** karte送信用リスト */
    let producerList: any;
    /** サーチから取得 */
    let producersData: KarteProducer[] = [];
    /** CMS APIから取得 */
    let producerArticles: KarteProducerArticle[] = [];

    try {
      // サーチから一覧表示フラグONのものしか取得してないため、20件リクエストじゃなくて全件取得する必要がある
      const { producers } = await store.dispatch('modules/content/fetchProducerDetails', { producerIds });
      // 生産者IDの登録降順で最新20件を抽出
      producersData = producerIds
        .map((id) => producers.find((producer) => producer.id === id) || null)
        .filter(nonNullable)
        .slice(0, NUM_OF_FAV_FOR_KARTE);
    } catch {
      producerList = [];
    }

    try {
      producerArticles = await store.dispatch('modules/content/fetchProducersForKarte', {
        permaLinks: producersData.map((producer) => producer.id),
      });
    } catch {
      // 何もしない（サーチのデータのみ送る）
    }

    const allTags = await fetchAllTags();

    // KARTEに必要なフィールドに整形
    producerList = producersData.map((searchData) => {
      const producerArticle = producerArticles.find((article: any) => article.permalink === searchData.id);
      return {
        article_id: parseInt(searchData.id),
        title: searchData.pageTitle,
        article_url: urlJoin(FRONTEND_ORIGIN, url('PRODUCER_DETAIL', { id: searchData.id })),
        article_type: ArticleType.Producers, // お気に入り種別を判別するために記事データなくても固定値として保留
        categories: convertProducerCategories(searchData.areas),
        main_image_pc: searchData.mainPcImage,
        main_image_sp: searchData.mainSpImage,
        tags:
          producerArticle?.tagIds
            .map((id: number) => allTags.find((tag) => tag.article_id === id).contents.tag_name)
            .join(',') || undefined,
        area1: searchData.areas.length ? searchData.areas[0].regions[0].name : undefined,
        area2:
          searchData.areas.length && searchData.areas[0].regions.length > 1
            ? searchData.areas[0].regions[1].name
            : undefined,
        area3:
          searchData.areas.length && searchData.areas[0].regions.length > 2
            ? searchData.areas[0].regions[2].name
            : undefined,
        producer: searchData.name,
        status: producerArticle ? 'publish' : undefined, // /view_articlesは公開中の記事のみ返す
        publish_at_date: producerArticle?.publishAt,
        expire_at_date: producerArticle?.expireAt,
        new_label_interval_from_date: searchData.newLabelStart,
        new_label_interval_to_date: searchData.newLabelEnd,
        vintage_new_label_interval_from_date: searchData.vintageNewLabelStart,
        vintage_new_label_interval_to_date: searchData.vintageNewLabelEnd,
      };
    });

    const infoToBeSent = {
      quantity: producerIds.length,
      status: producerIds.length > 0,
      add_article_id: isActive ? producerId : undefined,
      deleted_article_id: isActive ? undefined : producerId,
      articles: producerList,
      content_article_ids: filterItems(producerList.map((producer: any) => producer.article_id)),
      content_titles: filterItems(producerList.map((producer: any) => producer.title)),
      content_article_urls: filterItems(producerList.map((producer: any) => producer.article_url)),
      content_article_type: ArticleType.Producers,
      content_categories: filterItems(producerList.map((producer: any) => producer.categories)),
      content_main_image_pcs: filterItems(producerList.map((producer: any) => producer.main_image_pc)),
      content_main_image_sps: filterItems(producerList.map((producer: any) => producer.main_image_sp)),
      content_tags: filterItems(producerList.map((producer: any) => producer.tags)),
      content_area1s: filterItems(producerList.map((producer: any) => producer.area1)),
      content_area2s: filterItems(producerList.map((producer: any) => producer.area2)),
      content_area3s: filterItems(producerList.map((producer: any) => producer.area3)),
      content_producers: filterItems(producerList.map((producer: any) => producer.producer)),
      content_status: 'publish',
      /**
       * NOTE: undefinedの日付が入ってるとき先にDateに変換すると、invalid dateになるのでfilterItemsで除外できない
       * そのため、undefinedを除外してからDateに変換する必要がある
       */
      content_publish_at_dates: filterItems(producerList.map((producer: any) => producer.publish_at_date))?.map(
        (date: string) => new Date(date)
      ),
      content_expire_at_dates: filterItems(producerList.map((producer: any) => producer.expire_at_date))?.map(
        (date: string) => new Date(date)
      ),
      content_new_label_interval_from_dates: filterItems(
        producerList.map((producer: any) => producer.new_label_interval_from_date)
      )?.map((date: string) => new Date(date)),
      content_new_label_interval_to_dates: filterItems(
        producerList.map((producer: any) => producer.new_label_interval_from_date)
      )?.map((date: string) => new Date(date)),
      content_vintage_new_label_interval_from_dates: filterItems(
        producerList.map((producer: any) => producer.vintage_new_label_interval_from_date)
      )?.map((date: string) => new Date(date)),
      content_vintage_new_label_interval_to_dates: filterItems(
        producerList.map((producer: any) => producer.vintage_new_label_interval_to_date)
      )?.map((date: string) => new Date(date)),
    };

    karteTrack('favorite_content', infoToBeSent);
  };

  // 最新登録順で【公開中】の記事20件を送信（登録済み記事が非公開に変わった場合もあるので、最初は全件取得する必要がある）
  const setKarteFavoriteMagazineTag = async (articleId: number, isActive: boolean, categories: ArticleCategory[]) => {
    // NOTE: お気に入りAPIのレスポンスはすでに登録降順になってる
    const articleIds = store.state.user.favorites.favoriteArticles.map(({ favoriteCode }) => parseInt(favoriteCode));
    let magazines: any;
    try {
      magazines = await store.dispatch('modules/content/fetchRelatedMagazines', {
        articleIdList: articleIds,
        noListDisplayFlag: true,
      });
    } catch {
      magazines = [];
    }

    // 登録降順に並び替え；最初の２０件を抽出
    magazines = articleIds
      .map((id) => magazines.find((magazine: any) => id === parseInt(magazine.id)))
      .slice(0, NUM_OF_FAV_FOR_KARTE);

    const allTags = await fetchAllTags();

    // KARTEに必要なフィールドに整形
    magazines = magazines.map((magazine: any) => {
      return {
        article_id: parseInt(magazine.id),
        title: magazine.title,
        article_url: urlJoin(FRONTEND_ORIGIN, url('ARTICLE_DETAIL', { permalink: magazine.permalink })),
        article_type: ArticleType.Articles,
        categories: categories.find((category) => category.id === magazine.categoryId)?.name,
        main_image_pc: magazine.mainImagePc,
        main_image_sp: magazine.mainImageSp,
        tags: magazine.tagIds
          .map((id: number) => allTags.find((tag) => parseInt(tag.article_id) === id).contents.tag_name)
          .join(','),
        status: 'publish',
        publish_at_date: magazine.publishAt,
        expire_at_date: magazine.expireAt,
      };
    });

    const infoToBeSent = {
      quantity: articleIds.length,
      status: articleIds.length > 0,
      add_article_id: isActive ? articleId : undefined,
      deleted_article_id: isActive ? undefined : articleId,
      articles: magazines,
      content_article_ids: articleIds,
      content_titles: filterItems(magazines.map((magazine: any) => magazine.title)),
      content_article_urls: filterItems(magazines.map((magazine: any) => magazine.article_url)),
      content_article_type: ArticleType.Articles,
      content_categories: filterItems(magazines.map((magazine: any) => magazine.categories)),
      content_main_image_pcs: filterItems(magazines.map((magazine: any) => magazine.main_image_pc)),
      content_main_image_sps: filterItems(magazines.map((magazine: any) => magazine.main_image_sp)),
      content_tags: filterItems(magazines.map((magazine: any) => magazine.tags)),
      content_status: 'publish',
      content_publish_at_dates: filterItems(magazines.map((magazine: any) => new Date(magazine.publish_at_date))),
      content_expire_at_dates: filterItems(magazines.map((magazine: any) => new Date(magazine.expire_at_date))),
    };

    karteTrack('favorite_content', infoToBeSent);
  };

  // 第１階層＋第２階層を連携（第２階層がコード定義に存在しない場合は除外）
  const convertProducerCategories = (areas: ContentProducerArea[]) => {
    if (areas.length === 0) return;
    const categories = [];
    const area1 = areas[0].regions[0].enName;
    if (PRODUCER_CATEGORIES.find((element) => element.value === area1)) {
      categories.push(area1);
    } else {
      categories.push('OTHER');
    }
    const area2 = areas[0].regions.length > 1 ? areas[0].regions[1].enName : undefined;
    if (area2 && PRODUCER_CATEGORIES.find((element) => element.value === area2)) {
      categories.push(area2);
    }
    return categories.join(',');
  };

  const fetchAllTags = async () => {
    try {
      const { result = [] } = await store.dispatch('modules/content/fetchTags', {});
      return result;
    } catch {
      return [];
    }
  };

  return {
    setKarteFavoriteProductsTag,
    setKarteFavoriteProducersTag,
    setKarteFavoriteMagazineTag,
  };
};
